import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Logo from '@/components/Logo';
import EventBanner from '@/components/EventBanner';
import {
  Search,
  Calendar,
  Video,
  FolderOpen,
  Brain,
  Shield,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Stethoscope,
  Building2,
  Clock,
  Users,
  Lock,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';

const Landing = () => {
  // 4 fonctionnalités principales uniquement
  const mainFeatures = [
    {
      icon: Search,
      title: 'Trouvez un professionnel',
      description: 'Recherchez par spécialité, ville ou établissement et prenez rendez-vous en ligne.',
      color: 'bg-[#E53935]'
    },
    {
      icon: Video,
      title: 'Téléconsultation',
      description: 'Consultez un médecin à distance par vidéo, où que vous soyez.',
      color: 'bg-[#1976D2]'
    },
    {
      icon: FolderOpen,
      title: 'Dossier médical',
      description: 'Conservez ordonnances, analyses et vaccins dans un espace sécurisé.',
      color: 'bg-[#7C3AED]'
    },
    {
      icon: Brain,
      title: 'Assistant IA',
      description: 'Posez vos questions santé et préparez vos consultations.',
      color: 'bg-[#F59E0B]'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] overflow-x-hidden" data-testid="landing-page">
      <EventBanner />
      
      {/* ==================== HEADER ==================== */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="landing-logo">
            <Logo size="sm" showSlogan={false} />
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/login">
              <Button 
                variant="ghost" 
                className="text-slate-700 font-medium hover:text-[#E53935] rounded-full hidden sm:inline-flex"
                data-testid="nav-link-connexion"
              >
                Connexion
              </Button>
            </Link>
            <Link to="/register">
              <Button 
                className="bg-[#E53935] hover:bg-[#C62828] text-white font-semibold rounded-full px-6 py-2 shadow-lg"
                data-testid="nav-link-inscription"
              >
                S'inscrire
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ==================== SECTION 1: HERO ==================== */}
      <section className="pt-24 pb-12 md:pt-28 md:pb-16 px-4 sm:px-6 lg:px-8 relative" data-testid="hero-section">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-[#E53935]/5 to-transparent rounded-full blur-3xl -z-10" />
        
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Left: Text */}
            <div className="order-2 lg:order-1">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Votre santé, accessible depuis{' '}
                <span className="text-[#E53935]">votre téléphone</span>
              </h1>
              
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 max-w-lg">
                Trouvez un médecin, prenez rendez-vous en ligne, consultez à distance 
                et conservez vos documents médicaux en un seul endroit.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <Link to="/register">
                  <Button 
                    className="w-full sm:w-auto bg-[#E53935] hover:bg-[#C62828] text-white font-semibold rounded-full px-6 py-5 text-base shadow-lg"
                    data-testid="hero-cta-primary"
                  >
                    Créer mon compte gratuit
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/search">
                  <Button 
                    variant="outline"
                    className="w-full sm:w-auto border-slate-200 bg-white text-slate-700 font-medium rounded-full px-6 py-5 text-base"
                    data-testid="hero-cta-secondary"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Trouver un médecin
                  </Button>
                </Link>
              </div>

              {/* Trust badges inline */}
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <Shield className="w-4 h-4 text-[#1976D2]" />
                  Données sécurisées
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4 text-[#059669]" />
                  Disponible 24h/24
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-[#7C3AED]" />
                  100% gratuit
                </span>
              </div>
            </div>

            {/* Right: Hero Image */}
            <div className="order-1 lg:order-2">
              <img 
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_dockhta-health/artifacts/9l8waldc_dockhta%20l%27echosysteme%20de%20sante%20numerique%20en%20afrique.png"
                alt="DOCKHTA - Santé numérique en Afrique"
                className="w-full h-auto max-h-[450px] object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80';
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SECTION 2: 4 FONCTIONNALITÉS ==================== */}
      <section className="py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Tout ce dont vous avez besoin
            </h2>
            <p className="text-slate-600">Une seule application pour gérer votre santé</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {mainFeatures.map((feature, index) => (
              <Card 
                key={index}
                className="border-0 shadow-sm hover:shadow-md transition-all duration-300 bg-white rounded-xl"
              >
                <CardContent className="p-5">
                  <div className={`w-11 h-11 ${feature.color} rounded-xl flex items-center justify-center mb-3`}>
                    <feature.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{feature.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== SECTION 3: ASSISTANT IA (compact) ==================== */}
      <section className="py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1976D2]/5 to-[#7C3AED]/5" data-testid="ai-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left: Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F59E0B]/10 rounded-full mb-4">
                <Sparkles className="w-4 h-4 text-[#F59E0B]" />
                <span className="text-sm font-medium text-[#F59E0B]">Assistant IA</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Posez vos questions santé, à tout moment
              </h2>
              
              <ul className="space-y-2 mb-4">
                {[
                  'Comprendre vos symptômes',
                  'Préparer vos consultations',
                  'Expliquer une ordonnance'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-700">
                    <CheckCircle className="w-4 h-4 text-[#1976D2]" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>

              <p className="text-xs text-slate-500 bg-white/60 rounded-lg p-3 flex items-start gap-2">
                <Shield className="w-4 h-4 mt-0.5 text-[#1976D2] flex-shrink-0" />
                <span>L'IA ne remplace pas un médecin. Elle vous aide à mieux vous orienter.</span>
              </p>
            </div>

            {/* Right: Chat preview */}
            <div className="bg-white rounded-2xl p-5 shadow-lg border border-slate-100">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1976D2] to-[#7C3AED] flex items-center justify-center">
                  <Brain className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">Assistant DOCKHTA</p>
                  <p className="text-xs text-green-600">En ligne</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="bg-slate-100 rounded-xl rounded-tl-sm p-3 max-w-[85%]">
                  <p className="text-slate-700 text-sm">J'ai mal à la tête depuis 2 jours...</p>
                </div>
                <div className="bg-[#1976D2] rounded-xl rounded-tr-sm p-3 max-w-[85%] ml-auto">
                  <p className="text-white text-sm">Je comprends. Avez-vous d'autres symptômes comme de la fièvre ?</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SECTION 4: PROFESSIONNELS & ÉTABLISSEMENTS ==================== */}
      <section className="py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-[#0B1120]" data-testid="pro-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Vous êtes professionnel de santé ?
            </h2>
            <p className="text-slate-400">Développez votre visibilité et simplifiez votre pratique</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Professionnels */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#059669] flex items-center justify-center">
                  <Stethoscope className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Professionnels</h3>
                  <p className="text-slate-400 text-sm">Médecins, infirmiers, spécialistes</p>
                </div>
              </div>
              
              <ul className="space-y-2 mb-5">
                {['Profil vérifié et visible', 'Gestion des rendez-vous', 'Téléconsultation intégrée'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#059669]" />
                    {item}
                  </li>
                ))}
              </ul>
              
              <Link to="/rejoindre">
                <Button className="w-full bg-[#059669] hover:bg-[#047857] text-white rounded-xl" data-testid="pro-cta">
                  Rejoindre DOCKHTA
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Établissements */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#E53935] flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Établissements</h3>
                  <p className="text-slate-400 text-sm">Cliniques, centres médicaux, hôpitaux</p>
                </div>
              </div>
              
              <ul className="space-y-2 mb-5">
                {['Gestion de votre équipe', 'File d\'attente numérique', 'Tableau de bord complet'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#E53935]" />
                    {item}
                  </li>
                ))}
              </ul>
              
              <Link to="/register?type=establishment">
                <Button className="w-full bg-[#E53935] hover:bg-[#C62828] text-white rounded-xl" data-testid="facility-cta">
                  Inscrire mon établissement
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Agent terrain */}
          <div className="mt-6 text-center">
            <Link to="/devenir-agent" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors">
              <Users className="w-4 h-4" />
              Devenir agent terrain et gagner des commissions
              <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* ==================== SECTION 5: CTA FINAL ==================== */}
      <section className="py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Prêt à simplifier votre parcours santé ?
          </h2>
          <p className="text-slate-600 mb-6">
            Rejoignez des milliers d'utilisateurs qui font confiance à DOCKHTA
          </p>
          
          <Link to="/register">
            <Button 
              className="bg-[#E53935] hover:bg-[#C62828] text-white font-semibold rounded-full px-8 py-5 text-base shadow-lg"
              data-testid="final-cta"
            >
              Créer mon compte gratuitement
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-slate-500">
            <span className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-[#1976D2]" />
              Données chiffrées
            </span>
            <span className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#059669]" />
              Conforme RGPD
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#7C3AED]" />
              Professionnels vérifiés
            </span>
          </div>
        </div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="bg-[#0B1120] text-white py-12 px-4 sm:px-6 lg:px-8" data-testid="footer">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Logo & description */}
            <div className="sm:col-span-2 lg:col-span-1">
              <Logo size="sm" variant="white" showSlogan={false} className="mb-3" />
              <p className="text-slate-400 text-sm mb-4">
                La plateforme de santé numérique qui simplifie votre parcours médical au Cameroun.
              </p>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <MapPin className="w-4 h-4" />
                <span>Yaoundé, Cameroun</span>
              </div>
            </div>

            {/* Annuaires */}
            <div>
              <h4 className="font-semibold text-white mb-3 text-sm">Annuaires</h4>
              <ul className="space-y-2">
                <li><Link to="/professionnels" className="text-slate-400 hover:text-white text-sm">Professionnels de santé</Link></li>
                <li><Link to="/etablissements" className="text-slate-400 hover:text-white text-sm">Établissements</Link></li>
                <li><Link to="/pharmacies" className="text-slate-400 hover:text-white text-sm">Pharmacies</Link></li>
                <li><Link to="/search" className="text-slate-400 hover:text-white text-sm">Rechercher un médecin</Link></li>
              </ul>
            </div>

            {/* Professionnels */}
            <div>
              <h4 className="font-semibold text-white mb-3 text-sm">Rejoindre DOCKHTA</h4>
              <ul className="space-y-2">
                <li><Link to="/rejoindre" className="text-slate-400 hover:text-white text-sm">Professionnels de santé</Link></li>
                <li><Link to="/devenir-agent" className="text-slate-400 hover:text-white text-sm">★ Devenir agent terrain</Link></li>
                <li><Link to="/faq" className="text-slate-400 hover:text-white text-sm">Questions fréquentes</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold text-white mb-3 text-sm">Contact</h4>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-slate-400 text-sm">
                  <Mail className="w-4 h-4" />
                  <a href="mailto:contact@dockhta.com" className="hover:text-white">contact@dockhta.com</a>
                </li>
                <li className="flex items-center gap-2 text-slate-400 text-sm">
                  <Phone className="w-4 h-4" />
                  <a href="tel:+237621055365" className="hover:text-white">+237 621 055 365</a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-xs">© 2026 DOCKHTA. Tous droits réservés.</p>
            <div className="flex items-center gap-4 text-xs">
              <Link to="/privacy" className="text-slate-400 hover:text-white">Confidentialité</Link>
              <Link to="/mentions-legales" className="text-slate-400 hover:text-white">Mentions légales</Link>
              <Link to="/about" className="text-slate-400 hover:text-white">À propos</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
